import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadPermissions = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/rbac/my-permissions`, { withCredentials: true });
      setPermissions(res.data.modules || []);
    } catch {
      setPermissions([]);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      setUser(response.data);
      await loadPermissions();
    } catch (error) {
      setUser(false);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  }, [loadPermissions]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password }, { withCredentials: true });
    const data = response.data;
    // Handle new format: { user: {...} }
    const userData = data.user || data;
    setUser(userData);
    await loadPermissions();
    return userData;
  };

  const logout = async () => {
    await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    setUser(false);
    setPermissions([]);
  };

  const register = async (userData) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, userData, { withCredentials: true });
    setUser(response.data);
    return response.data;
  };

  const hasPermission = (moduleKey) => {
    if (!user) return false;
    if (user.role === 'Super Admin') return true;
    return permissions.includes(moduleKey);
  };

  return (
    <AuthContext.Provider value={{ user, loading, permissions, login, logout, register, checkAuth, hasPermission, loadPermissions }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
